import { useCallback, useEffect, useRef, useState } from 'react'
import { Chart, registerables } from 'chart.js'
import { bettingApi } from '../../api'
import { useAuth } from '../../AuthContext'
import { token } from '../../tokens'
import { ErrorState, errorMessage } from '../ErrorState'
import type { BreachType, ComplianceSeverity, PaymentTestRow, PaymentTestSummary } from '../../types'
import { periodRange, PERIOD_OPTIONS, useShell } from '../../shell/ShellContext'
import { navigate, withQuery } from '../../shell/router'
import { isManagerRole } from '../../shell/navigation'
import { BarList, Block, Kpi, PageHeader, StatusPill, usePercent } from '../../ui/ui'
import { useI18n } from '../../i18n'
import type { TFunction } from '../../i18n'
import {
  aggregatorStatusLabel,
  breachLabel,
  FixtureToggle,
  severityLabel,
  SEVERITY_ORDER,
  countryLabel,
  useBettingVocabulary,
} from './bettingShared'
import { ManualTestPanel, TestDetailPage, TestList } from './BettingTests'
import { BettingServices } from './BettingServices'
import { BettingReferentials } from './BettingReferentials'
import { BettingQualify } from './BettingQualify'
import { BettingDiscovery } from './BettingDiscovery'
import './betting.css'
import './betting-screens.css'

Chart.register(...registerables)

/** Couleur de chaque gravité, reprise des pastilles pour que le graphique et la liste se lisent ensemble. */
export const SEVERITY_COLORS: Record<ComplianceSeverity | 'UNQUALIFIED', string> = {
  HIGH: '--crit',
  MEDIUM: '#EA580C',
  LOW: '#EAB308',
  TECHNICAL_ISSUE: '--text-hint',
  COMPLIANT: '--ok-fg',
  UNQUALIFIED: '--border',
}

/**
 * Paris & jeux : conformité des parcours de paiement sur les sites de paris.
 * Chaque sous-écran a sa propre adresse (#/betting/tests/…).
 */
export function BettingModule({ path }: { path: string[] }) {
  const { user } = useAuth()
  const canManage = isManagerRole(user)
  const [view, id] = path

  switch (view) {
    case 'tests':
      return id ? <TestDetailPage testId={id} /> : <TestList />
    case 'services':
      return <BettingServices canManage={canManage} />
    case 'discovery':
      return <BettingDiscovery canManage={canManage} />
    case 'qualify':
      return <BettingQualify canManage={canManage} />
    case 'referentials':
      return <BettingReferentials canManage={canManage} />
    default:
      return <BettingDashboard />
  }
}

function BettingDashboard() {
  const { t, fmt } = useI18n()
  const percent = usePercent()
  const { country, period, showFixtures, setShowFixtures } = useShell()
  const vocab = useBettingVocabulary()
  const [summary, setSummary] = useState<PaymentTestSummary | null>(null)
  const [robotTests, setRobotTests] = useState<number | null>(null)
  const [priorities, setPriorities] = useState<PaymentTestRow[] | null>(null)
  const [priorityTotal, setPriorityTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const range = periodRange(period)
  const scope = {
    country: country || undefined,
    from: range.from,
    to: range.to,
    includeFixtures: showFixtures ? '1' : undefined,
  }
  const scopeKey = `${country}|${range.from}|${range.to}|${showFixtures}`

  const load = useCallback(() => {
    setError(null)
    Promise.all([
      bettingApi.summary(scope),
      bettingApi.listTests({ ...scope, source: 'AUTO', limit: 1 }),
      bettingApi.listTests({ ...scope, severity: 'HIGH', open: '1', limit: 6 }),
    ])
      .then(([s, robot, high]) => {
        setSummary(s)
        setRobotTests(robot.total)
        setPriorities(high.data)
        setPriorityTotal(high.total)
      })
      .catch((err) => setError(errorMessage(err)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  useEffect(load, [load])

  const option = PERIOD_OPTIONS.find((p) => p.value === period)
  const periodLabel = option ? t(option.labelKey).toLowerCase() : t('period.days30').toLowerCase()
  const perimeter = country ? countryLabel(vocab, country, t) : t('bet.allScopeCountries')
  const drill = (params: Record<string, string>) => navigate(withQuery('betting/tests', params))

  return (
    <>
      <PageHeader
        title={t('bet.dashTitle')}
        description={t('bet.dashDesc', { period: periodLabel, perimeter })}
        actions={
          <>
            <FixtureToggle checked={showFixtures} onChange={setShowFixtures} />
            <button type="button" className="btn btn-secondary" onClick={() => setCreating(true)}>{t('bet.manualTest')}</button>
            <button type="button" className="btn btn-primary" onClick={() => navigate('betting/discovery')}>{t('bet.findSites')}</button>
          </>
        }
      />

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !summary ? (
        <div className="stat-skeleton" style={{ minHeight: 320 }} />
      ) : (
        <>
          <div className="grid-kpi">
            <Kpi
              label={t('bet.kpiTests')}
              value={fmt.number(summary.total)}
              sub={robotTests !== null ? t('bet.kpiTestsSub', { robot: robotTests, agents: summary.total - robotTests }) : undefined}
              onClick={() => drill({})}
            />
            <Kpi
              label={t('bet.kpiNotCompliant')}
              value={percent(summary.notCompliant, summary.total)}
              sub={t('bet.kpiNotCompliantSub', { n: summary.notCompliant, high: summary.bySeverity.HIGH })}
              tone={summary.bySeverity.HIGH > 0 ? 'crit' : undefined}
            />
            <Kpi label={t('bet.kpiOpen')} value={fmt.number(summary.open)} sub={t('bet.kpiOpenSub', { n: summary.closed })} onClick={() => drill({ open: '1' })} />
            <Kpi
              label={t('bet.kpiToQualify')}
              value={fmt.number(summary.pendingCandidates)}
              sub={t('bet.kpiToQualifySub')}
              tone={summary.pendingCandidates > 0 ? 'warn' : undefined}
              onClick={() => navigate('betting/qualify')}
            />
          </div>

          <div className="grid-main-side">
            <Block
              title={t('bet.priorityTitle')}
              subtitle={t('bet.prioritySub')}
              action={
                priorities && priorities.length > 0 ? (
                  <button type="button" className="block-link" onClick={() => drill({ severity: 'HIGH', open: '1' })}>
                    {t('bet.seeCases', { n: priorityTotal })}
                  </button>
                ) : undefined
              }
            >
              {!priorities || priorities.length === 0 ? (
                <p className="empty-note">{t('bet.noPriority')}</p>
              ) : (
                <ul className="row-list">
                  {priorities.map((row) => (
                    <li key={row.id}>
                      <button type="button" className="row-link" onClick={() => navigate(`betting/tests/${row.id}`)}>
                        <i className="bet-dot bet-dot-high" aria-hidden="true" />
                        <span className="row-main">
                          <strong>{row.serviceName}</strong>
                          <span>{t('bet.testNumber', { n: row.testNumber })} · {countryLabel(vocab, row.country, t)}</span>
                        </span>
                        <span className="row-meta bet-hide-narrow">{row.breachType ? breachLabel(t, row.breachType) : t('bet.breachToSpecify')}</span>
                        <span className="row-meta bet-hide-narrow">{row.assignedTo ?? t('bet.notAssigned')}</span>
                        <span className="row-open">{t('common.open')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Block>

            <Block title={t('bet.severityBlock')}>
              <div className="bet-severity-summary">
                <ComplianceDonut summary={summary} />
                <ul className="bet-severity-legend">
                  {SEVERITY_ORDER.map((s) => (
                    <li key={s}>
                      <button type="button" onClick={() => drill({ severity: s })}>
                        <i className="bet-dot" style={{ background: token(SEVERITY_COLORS[s]) }} aria-hidden="true" />
                        <span>{severityLabel(t, s)}</span>
                        <strong className="num">{fmt.number(summary.bySeverity[s])}</strong>
                      </button>
                    </li>
                  ))}
                  {summary.bySeverity.UNQUALIFIED > 0 && (
                    <li>
                      <span className="bet-severity-legend-static">
                        <i className="bet-dot bet-dot-unqualified" aria-hidden="true" />
                        <span>{t('bet.sev.notQualified')}</span>
                        <strong className="num">{fmt.number(summary.bySeverity.UNQUALIFIED)}</strong>
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </Block>
          </div>

          <div className="grid-2">
            <Block title={t('bet.breachesBlock')}>
              {Object.keys(summary.byBreach).length === 0 ? (
                <p className="empty-note">{t('bet.noBreach')}</p>
              ) : (
                <BarList
                  rows={(Object.entries(summary.byBreach) as Array<[BreachType, number]>)
                    .sort((a, b) => b[1] - a[1])
                    .map(([b, n]) => ({ key: b, label: breachLabel(t, b), count: n, color: token('--crit') }))}
                  onSelect={(b) => drill({ breachType: b })}
                />
              )}
            </Block>
            <Block
              title={t('bet.aggregatorsBlock')}
              subtitle={t('bet.aggregatorsBlockSub')}
              action={<button type="button" className="block-link" onClick={() => navigate('betting/referentials')}>{t('bet.referentialLink')}</button>}
            >
              {summary.topAggregators.length === 0 ? (
                <p className="empty-note">{t('bet.noAggregator')}</p>
              ) : (
                <ul className="row-list">
                  {summary.topAggregators.map((a) => (
                    <li key={a.aggregatorId ?? a.name}>
                      <button
                        type="button"
                        className="row-link"
                        disabled={!a.aggregatorId}
                        onClick={() => a.aggregatorId && drill({ aggregatorId: a.aggregatorId })}
                      >
                        <span className="row-main">
                          <strong>{a.name}</strong>
                          {a.notCompliant > 0 && <span>{t('bet.notCompliantCount', { n: a.notCompliant })}</span>}
                        </span>
                        <span className="row-meta num">{t('bet.testCount', { n: a.tests })}</span>
                        <StatusPill tone={a.status === 'AUTHORIZED' ? 'ok' : a.status === 'UNAUTHORIZED' ? 'crit' : 'info'}>
                          {aggregatorStatusLabel(t, a.status)}
                        </StatusPill>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          </div>
        </>
      )}

      {creating && (
        <ManualTestPanel
          onClose={() => setCreating(false)}
          onCreated={(testId) => {
            setCreating(false)
            navigate(`betting/tests/${testId}`)
          }}
        />
      )}
    </>
  )
}

function ComplianceDonut({ summary }: { summary: PaymentTestSummary }) {
  const { t } = useI18n()
  const percent = usePercent()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return
    chartRef.current?.destroy()
    const keys = [...SEVERITY_ORDER, 'UNQUALIFIED'] as const
    // Les canvas ne résolvent pas les variables CSS : on passe par token().
    chartRef.current = new Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels: keys.map((k) => (k === 'UNQUALIFIED' ? t('bet.sev.notQualified') : severityLabel(t, k))),
        datasets: [
          {
            data: keys.map((k) => summary.bySeverity[k]),
            backgroundColor: keys.map((k) => token(SEVERITY_COLORS[k])),
            borderWidth: 0,
          },
        ],
      },
      options: { cutout: '76%', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
    })
    return () => chartRef.current?.destroy()
  }, [summary, t])

  return (
    <div className="bet-donut-wrap">
      <div className="bet-donut" role="img" aria-label={t('bet.donutAria', { compliant: summary.compliant, total: summary.total })}>
        <canvas ref={canvasRef} />
      </div>
      <div className="bet-donut-center" aria-hidden="true">
        <span className="num">{percent(summary.compliant, summary.total)}</span>
        <span>{t('bet.donutCenter')}</span>
      </div>
    </div>
  )
}
